import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
    LayoutDashboard, 
    Users, 
    LogOut, 
    Menu, 
    X,
    ChevronRight
} from 'lucide-react';
import { getAdminInfo, adminLogout } from '@/lib/api';
import { cn } from '@/lib/utils';

const sidebarLinks = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Applications', path: '/admin/applications', icon: Users },
];

/**
 * AdminLayout Component
 * This component ONLY handles layout rendering.
 * Authentication is handled by ProtectedRoute wrapper.
 * NO redirects happen here - that would cause React DOM errors.
 */
const AdminLayout = () => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Fetch user info on mount (auth already verified by ProtectedRoute)
    useEffect(() => {
        // Add noindex meta tag for admin pages
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);

        const fetchUser = async () => {
            try {
                const adminData = await getAdminInfo();
                setUser(adminData);
            } catch (error) {
                console.error('Error fetching admin info:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUser();

        return () => {
            document.head.removeChild(meta);
        };
    }, []);

    const handleLogout = () => {
        adminLogout();
        navigate('/admin/login', { replace: true });
    };

    // Loading state while fetching user data
    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 rounded-lg bg-primary animate-pulse mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar - Desktop */}
            <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 admin-sidebar">
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 border-b border-primary-foreground/10">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary-foreground/10 border border-primary-foreground/30 flex items-center justify-center">
                                <span className="font-display font-bold text-lg text-primary-foreground">
                                    GM
                                </span>
                            </div>
                            <div>
                                <p className="font-display font-semibold text-primary-foreground">
                                    Girne MUN
                                </p>
                                <p className="text-xs text-primary-foreground/60">
                                    Admin Panel
                                </p>
                            </div>
                        </Link>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1">
                        {sidebarLinks.map((link) => {
                            const isActive = location.pathname === link.path || 
                                (link.path !== '/admin' && location.pathname.startsWith(link.path));
                            
                            return (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    data-testid={`sidebar-link-${link.name.toLowerCase()}`}
                                    className={cn(
                                        "admin-sidebar-item",
                                        isActive && "active"
                                    )}
                                >
                                    <link.icon className="w-5 h-5" />
                                    <span>{link.name}</span>
                                    {isActive && (
                                        <ChevronRight className="w-4 h-4 ml-auto" />
                                    )}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User & Logout */}
                    <div className="p-4 border-t border-primary-foreground/10">
                        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary-foreground/5 mb-3">
                            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                                <span className="text-accent-foreground font-semibold text-sm">
                                    {user?.name?.charAt(0) || 'A'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-primary-foreground text-sm font-medium truncate">
                                    {user?.name || 'Admin'}
                                </p>
                                <p className="text-primary-foreground/60 text-xs truncate">
                                    {user?.email}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            data-testid="logout-btn"
                            className="w-full admin-sidebar-item justify-center hover:bg-destructive/20 hover:text-destructive"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Sign Out</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-primary/80 z-40"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Mobile Sidebar */}
            <aside className={cn(
                "lg:hidden fixed inset-y-0 left-0 w-64 z-50 admin-sidebar transform transition-transform duration-300",
                isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex flex-col h-full">
                    <div className="p-4 flex items-center justify-between border-b border-primary-foreground/10">
                        <span className="font-display font-semibold text-primary-foreground">Menu</span>
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            className="p-2 rounded-lg hover:bg-primary-foreground/10 text-primary-foreground"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <nav className="flex-1 p-4 space-y-1">
                        {sidebarLinks.map((link) => {
                            const isActive = location.pathname === link.path;
                            
                            return (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setIsSidebarOpen(false)}
                                    className={cn(
                                        "admin-sidebar-item",
                                        isActive && "active"
                                    )}
                                >
                                    <link.icon className="w-5 h-5" />
                                    <span>{link.name}</span>
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="p-4 border-t border-primary-foreground/10">
                        <button
                            onClick={handleLogout}
                            className="w-full admin-sidebar-item justify-center"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Sign Out</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 lg:pl-64">
                {/* Mobile Header */}
                <header className="lg:hidden sticky top-0 z-30 bg-background border-b border-border px-4 py-3">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setIsSidebarOpen(true)}
                            className="p-2 rounded-lg hover:bg-muted"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                        <span className="font-display font-semibold">Admin Panel</span>
                        <div className="w-10" />
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
